import { useEffect, useState } from 'react';
import { AppConfig } from '../types/app';
import { GandalfUser, MigrationStatus } from '../types/auth';
import {
  BE_TO_FE_ATTR_MAPPING,
  NEEDS_VERIFICATION_ATTRIBUTE,
  VIBE_ATTR_PATH,
} from '../constants/api';
import { CHECK_NEEDS_VERIFICATION } from '../constants/metrics';
import { request, joinUrlPath } from '../utils/apiHelper';
import { REQUIRE_EMAIL_VERIFICATION } from '../constants/auth';

const useVibeAttributes = (
  config: AppConfig,
  user: GandalfUser,
  params: URLSearchParams
) => {
  const [needsVerification, setNeedsVerification] = useState<
    boolean | undefined
  >(undefined);
  const [error, setError] = useState<any | undefined>(undefined);

  useEffect(() => {
    // If EVS is required with URL param or with claim on token, abort making fetch call
    if (
      params.get(REQUIRE_EMAIL_VERIFICATION) === 'true' ||
      user.email_verification_required === true
    ) {
      setNeedsVerification(false);
      return;
    }

    const fetchAttributes = async () => {
      const isWorkflowEnabled =
        config.enableSkillBuilderInvitationWorkflow === 'true';
      const isEmailUnverified = user?.email_verified === false;
      const hasEmail = user?.email !== undefined && user?.email !== '';
      const isEligibleMigrationStatus =
        user.migration_status === undefined ||
        user.migration_status === MigrationStatus.NOT_MIGRATED;

      const shouldFetchAttributes =
        isWorkflowEnabled &&
        isEmailUnverified &&
        hasEmail &&
        isEligibleMigrationStatus;
      if (shouldFetchAttributes) {
        try {
          const resp = await vibeGetAttributes(config, user);

          let profileMap: Map<string, string> = new Map();
          if (resp.returnItems) {
            resp.returnItems.forEach((attribute: any) => {
              const supportedAttr = BE_TO_FE_ATTR_MAPPING.get(attribute.name);
              if (supportedAttr !== undefined) {
                profileMap.set(supportedAttr, attribute.value.S);
              }
            });
          }
          if (profileMap.get(NEEDS_VERIFICATION_ATTRIBUTE) !== undefined) {
            setNeedsVerification(
              profileMap.get(NEEDS_VERIFICATION_ATTRIBUTE) === 'true'
            );
          } else {
            setNeedsVerification(false);
          }
        } catch (error: any) {
          setError(error);
        }
      } else {
        setNeedsVerification(false);
      }
    };
    fetchAttributes();
  }, [config, user?.email_verified, user, params]);

  return { needsVerification, error };
};

export const vibeGetAttributes = async (
  config: AppConfig,
  user: GandalfUser
) => {
  if (!user.jwtToken) {
    throw new Error('User toke not defined');
  }

  const opts: RequestInit = {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Authorization: user.jwtToken,
    },
  };
  const resp = await request(
    joinUrlPath(config.gandalfProfileEndpoint, VIBE_ATTR_PATH),
    opts,
    {
      metricNs: CHECK_NEEDS_VERIFICATION,
    }
  );

  return resp.json();
};

export default useVibeAttributes;
